import { useEffect, useMemo } from 'react';

import { AttributeOptions, CreateServiceFormValues } from './types';

export interface UsePrioritySelectionArgs {
  priorities: AttributeOptions['priorities'];
  initialPriorityCode: string;
  priorityCode: string;
  setValues: React.Dispatch<React.SetStateAction<CreateServiceFormValues>>;
}

export interface UsePrioritySelectionResult {
  selectedRules: string[];
}

// Encapsulates priority-derived behaviour: extracting service-creation rules
// from the chosen priority and applying the upstream-resolved default when
// the available list changes.
export const usePrioritySelection = ({
  priorities,
  initialPriorityCode,
  priorityCode,
  setValues,
}: UsePrioritySelectionArgs): UsePrioritySelectionResult => {
  // priorityTypes() returns numeric ids in `value`; priorityCode is stored as a
  // string. Coerce to compare safely (mirrors useRepairJobAttributes.ts).
  const selectedRules = useMemo<string[]>(() => {
    const priority = priorities.find((p) => String(p.value) === priorityCode);
    const rules = (priority as { selectedServiceCreationTypeValue?: unknown } | undefined)
      ?.selectedServiceCreationTypeValue;
    return Array.isArray(rules) ? (rules as string[]) : [];
  }, [priorities, priorityCode]);

  useEffect(() => {
    if (priorities.length === 0) return;
    const stillValid = priorities.some((p) => String(p.value) === priorityCode);
    if (stillValid) return;
    setValues((prev) => ({ ...prev, priorityCode: initialPriorityCode }));
    // priorityCode and setValues intentionally omitted: this should react only
    // to the option list (and its resolved default) changing, not to the
    // user's own picks.
  }, [priorities, initialPriorityCode]);

  return { selectedRules };
};
